import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Dot,
} from 'recharts';
import { formatSpread } from '../utils/formatters';
import {
  buildCurrentEntry,
  computePctVsMedian,
  formatNormalizedCostPct,
  formatPctVsMedian,
  getCostIndexColor,
  normalizedCostToBps,
} from '../utils/fusionCostIndex';
import { describeSimilarityKey } from '../utils/fusionSimilarityKey';

const formatDateLabel = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatUsdFromPoints = (points) => {
  if (!Number.isFinite(points)) return '—';
  return `$${Math.round(points * 100).toLocaleString('en-US')}`;
};

const HistoricalCostModal = ({ row, onClose }) => {
  const costIndex = row?._costIndex;
  const currentEntry = useMemo(() => buildCurrentEntry(row), [row]);

  const chartData = useMemo(() => {
    const points = [];
    (costIndex?.snapshots ?? []).forEach((snap) => {
      if (!Number.isFinite(snap.normalized_cost)) return;
      points.push({
        ts: snap.scan_timestamp,
        label: formatDateLabel(snap.scan_timestamp),
        normalizedCost: snap.normalized_cost,
        bps: normalizedCostToBps(snap.normalized_cost),
        totalDebit: snap.total_debit,
        spxRef: snap.spx_ref,
        isCurrent: false,
      });
    });

    if (currentEntry?.normalized_cost != null) {
      const currentTs = row?.cached_at || new Date().toISOString();
      points.push({
        ts: currentTs,
        label: 'Now',
        normalizedCost: currentEntry.normalized_cost,
        bps: normalizedCostToBps(currentEntry.normalized_cost),
        totalDebit: currentEntry.total_debit,
        spxRef: currentEntry.spx_ref,
        isCurrent: true,
      });
    }

    points.sort((a, b) => new Date(a.ts) - new Date(b.ts));
    return points;
  }, [costIndex, currentEntry, row]);

  const pctVsMedian = costIndex?.pct_vs_median ?? computePctVsMedian(
    currentEntry?.normalized_cost,
    costIndex?.median
  );

  const tableRows = useMemo(() => {
    const median = costIndex?.median;
    return chartData.map((point) => {
      const deltaVsMedian = Number.isFinite(median) && median > 0 && Number.isFinite(point.normalizedCost)
        ? Math.round(((point.normalizedCost / median) - 1) * 1000) / 10
        : null;
      return { ...point, deltaVsMedian };
    }).reverse();
  }, [chartData, costIndex?.median]);

  if (!row) return null;

  const cal = formatSpread(row.calendar_spread, 'calendar', row.calendar_quantity);
  const diag = formatSpread(row.diagonal_spread, 'diagonal', row.diagonal_quantity);
  const vert = formatSpread(row.vertical_spread, 'vertical');

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div className="history-modal-overlay" onClick={handleOverlayClick} role="presentation">
      <div className="history-modal" role="dialog" aria-modal="true" aria-labelledby="history-modal-title">
        <div className="history-modal-header">
          <div>
            <h2 id="history-modal-title">Historical Cost — SPX-normalized</h2>
            <p className="history-modal-subtitle">
              {cal} · {diag} · {vert}
            </p>
          </div>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="history-modal-body">
          <div className="history-modal-summary">
            <div className="history-summary-card">
              <span className="history-summary-label">Current Cost</span>
              <strong>{formatUsdFromPoints(currentEntry?.total_debit ?? row.total_debit)}</strong>
            </div>
            <div className="history-summary-card">
              <span className="history-summary-label">vs Historical Median</span>
              <strong style={{ color: getCostIndexColor(pctVsMedian) }}>
                {formatPctVsMedian(pctVsMedian)}
              </strong>
            </div>
            <div className="history-summary-card">
              <span className="history-summary-label">Norm. Cost (now)</span>
              <strong>{formatNormalizedCostPct(currentEntry?.normalized_cost)}</strong>
            </div>
            <div className="history-summary-card">
              <span className="history-summary-label">Snapshots</span>
              <strong>{costIndex?.count ?? 0}</strong>
            </div>
          </div>

          {!costIndex?.has_sufficient_history ? (
            <div className="history-modal-empty">
              Not enough historical data yet (need at least 3 snapshots for this structure).
            </div>
          ) : (
            <>
              <div className="history-chart-wrap">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: 'var(--muted)', fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fill: 'var(--muted)', fontSize: 11 }}
                      tickFormatter={(v) => `${Number(v).toFixed(2)} bps`}
                      width={56}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0]?.payload;
                        if (!p) return null;
                        return (
                          <div className="history-chart-tooltip">
                            <div>{formatDateTime(p.ts)}{p.isCurrent ? ' (current)' : ''}</div>
                            <div>SPX: {Number.isFinite(p.spxRef) ? p.spxRef.toFixed(2) : '—'}</div>
                            <div>Cost: {formatUsdFromPoints(p.totalDebit)}</div>
                            <div>Norm: {formatNormalizedCostPct(p.normalizedCost)}</div>
                          </div>
                        );
                      }}
                    />
                    {Number.isFinite(costIndex?.p25) && Number.isFinite(costIndex?.p75) && (
                      <ReferenceArea
                        y1={normalizedCostToBps(costIndex.p25)}
                        y2={normalizedCostToBps(costIndex.p75)}
                        fill="var(--accent)"
                        fillOpacity={0.08}
                        strokeOpacity={0}
                      />
                    )}
                    {Number.isFinite(costIndex?.median) && (
                      <ReferenceLine
                        y={normalizedCostToBps(costIndex.median)}
                        stroke="var(--muted)"
                        strokeDasharray="4 4"
                        label={{ value: 'Median', fill: 'var(--muted)', fontSize: 11, position: 'insideTopRight' }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="bps"
                      stroke="var(--accent)"
                      strokeWidth={2}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (payload?.isCurrent) {
                          return (
                            <Dot
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill="var(--accent)"
                              stroke="var(--surface)"
                              strokeWidth={2}
                            />
                          );
                        }
                        return <Dot cx={cx} cy={cy} r={3} fill="var(--accent)" />;
                      }}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <p className="history-chart-caption">
                  Y-axis: normalized cost in basis points of SPX (total debit ÷ SPX × 10,000). Shaded band: 25th–75th percentile.
                </p>
              </div>

              <div className="history-table-wrap">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>SPX</th>
                      <th>Cost</th>
                      <th>Norm. Cost</th>
                      <th>Δ vs Median</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((point) => (
                      <tr key={`${point.ts}-${point.isCurrent ? 'now' : 'hist'}`} className={point.isCurrent ? 'is-current' : ''}>
                        <td>{point.isCurrent ? 'Now' : formatDateTime(point.ts)}</td>
                        <td>{Number.isFinite(point.spxRef) ? point.spxRef.toFixed(2) : '—'}</td>
                        <td>{formatUsdFromPoints(point.totalDebit)}</td>
                        <td>{formatNormalizedCostPct(point.normalizedCost)}</td>
                        <td style={{ color: getCostIndexColor(point.deltaVsMedian) }}>
                          {formatPctVsMedian(point.deltaVsMedian)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <p className="history-modal-footnote">
            Matched by: {describeSimilarityKey(row._similarityKey) || '—'}.
            Short deltas encode moneyness (SPX-independent). One row per scan; multiple exact matches in the same scan use median cost.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HistoricalCostModal;
